import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend,
} from "recharts";
import { api } from "../lib/api";
import { fmtBRL, fmtPct } from "../lib/format";
import { computeDelta } from "../lib/delta";
import { useFilters, periodToDates } from "../lib/filters";
import { KPIStatCard } from "../components/KPIStatCard";
import { ChartCard } from "../components/ChartCard";
import { PageHeader } from "../components/PageHeader";
import {
  IconAlertTriangle, IconInfo,
  IconLineChart, IconCalculator,
} from "../components/icons";
import { CostsPage } from "./CostsPage";
import type { TransportDreDto, TransportDreLine } from "@bi/types";

type DreTab = "dre" | "custos";

// ── helpers ──────────────────────────────────────────────────────────────────

const MONTHS_PT = ["Jan","Fev","Mar","Abr","Mai","Jun","Jul","Ago","Set","Out","Nov","Dez"];

function fmtMonthShort(ym: string): string {
  const [, m] = ym.split("-");
  return MONTHS_PT[(parseInt(m ?? "1", 10) - 1) % 12] ?? ym;
}

function fmtMonth2026(ym: string): string {
  const [y, m] = ym.split("-");
  return `${MONTHS_PT[(parseInt(m ?? "1", 10) - 1) % 12]}/${(y ?? "26").slice(2)}`;
}

function fmtPeriodDate(iso: string): string {
  return new Date(iso).toLocaleDateString("pt-BR", {
    day: "2-digit", month: "2-digit", year: "numeric",
    timeZone: "America/Sao_Paulo",
  });
}

function axisFormatter(v: number): string {
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000)     return `${(v / 1_000).toFixed(0)}k`;
  return String(v);
}

// ── DRE table row ─────────────────────────────────────────────────────────────

/**
 * Drill por linha da DRE: linhas com FONTE navegam para a memória de cálculo
 * (mesmo período da tela); linhas DERIVADAS expandem a fórmula inline.
 * Regras em docs/auditoria/MAPA_REGRAS_FINANCEIRAS.md §5.
 */
const LINE_DRILL: Record<string, { cardKey: string } | { formula: string }> = {
  gross_revenue:  { cardKey: "cte-receita-mes" },
  cte_revenue:    { cardKey: "cte-receita-mes" },
  deductions:     { cardKey: "cte-icms-mes" },
  icms:           { cardKey: "cte-icms-mes" },
  net_revenue:    { formula: "Receita Líquida = Receita Bruta − ICMS s/ serviços" },
  direct_costs:   { formula: "Custos Diretos = Frete motorista + Pedágios + Sec/Cat e Despacho" },
  driver_freight: { cardKey: "dre-frete-motorista" },
  tolls:          { cardKey: "cte-pedagio-mes" },
  other_direct:   { formula: "Sec/Cat e Despacho = colunas do próprio CT-e (compõem o mesmo documento; abra Faturamento CT-e para ver os CT-es)" },
  contribution:   { formula: "Margem de Contribuição = Receita Líquida − Custos Diretos" },
  op_expenses:    { cardKey: "pago-no-periodo" },
  net_result:     { formula: "Resultado Estimado = Margem de Contribuição − Despesas Operacionais (⚠️ despesas incluem itens não operacionais — valor estimado)" },
};

function DreTableRow({
  line,
  prevValue,
  onClick,
  drillHint,
}: {
  line: TransportDreLine;
  prevValue: number | undefined;
  onClick?: () => void;
  drillHint?: "docs" | "formula";
}) {
  const isResult = line.role === "result";
  const isTotal  = line.role === "total";
  const isItem   = line.role === "item";

  const variation =
    prevValue != null && prevValue !== 0
      ? ((line.value - prevValue) / Math.abs(prevValue)) * 100
      : null;

  const variationColor =
    variation == null || Math.abs(variation) < 0.5
      ? "text-gray-400"
      : (line.isPositive && variation > 0) || (!line.isPositive && variation < 0)
      ? "text-tm-600"
      : "text-danger-600";

  return (
    <tr
      onClick={onClick}
      title={onClick ? (drillHint === "formula" ? "Clique para ver a fórmula" : "Clique para ver os documentos que compõem esta linha") : undefined}
      className={
        (onClick ? "cursor-pointer " : "") +
        (isResult
          ? line.value >= 0
            ? "bg-tm-50 border-l-4 border-l-tm-400"
            : "bg-danger-50 border-l-4 border-l-danger-400"
          : isTotal
          ? "bg-gray-50/70 hover:bg-gray-100/70"
          : "hover:bg-gray-50/50")
      }
    >
      {/* Label */}
      <td className="px-5 py-3">
        <div className={`flex items-center gap-2 ${isItem ? "pl-5" : ""}`}>
          <span
            className={
              isResult ? "font-bold text-gray-900 text-sm"
              : isTotal ? "font-semibold text-gray-800 text-sm"
              : "text-gray-600 text-sm"
            }
          >
            {line.label}
          </span>
          {onClick && drillHint === "docs" && (
            <span className="text-[10px] text-tm-600 opacity-70 flex-shrink-0">▸ docs</span>
          )}
          {onClick && drillHint === "formula" && (
            <span className="text-[10px] text-gray-400 opacity-70 flex-shrink-0">ƒx</span>
          )}
          {line.unreliable && (
            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md text-xs bg-warning-100 text-warning-700 font-medium flex-shrink-0">
              <IconAlertTriangle className="w-3 h-3" />
              Estimado
            </span>
          )}
        </div>
      </td>

      {/* Value */}
      <td className="px-5 py-3 text-right whitespace-nowrap">
        <span
          className={
            isResult
              ? line.value >= 0
                ? "font-bold text-tm-700 text-sm"
                : "font-bold text-danger-700 text-sm"
              : isTotal
              ? "font-semibold text-gray-900 text-sm"
              : "text-gray-700 text-sm"
          }
        >
          {line.formatted}
        </span>
      </td>

      {/* Variation */}
      <td className="px-5 py-3 text-right whitespace-nowrap hidden md:table-cell">
        {variation != null && Math.abs(variation) >= 0.5 ? (
          <span className={`text-xs font-medium ${variationColor}`}>
            {variation > 0 ? "+" : ""}{variation.toFixed(1)}%
          </span>
        ) : (
          <span className="text-xs text-gray-300">—</span>
        )}
      </td>

      {/* Source */}
      <td className="px-5 py-3 text-xs text-gray-400 hidden lg:table-cell max-w-[220px] truncate">
        {line.source ?? "—"}
      </td>
    </tr>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

function DreManagerialTab() {
  const navigate = useNavigate();
  const { filters } = useFilters();
  const { from, to } = periodToDates(filters.period, filters.customFrom, filters.customTo);
  // Linha derivada com fórmula expandida (clique em linhas ƒx)
  const [expandedFormula, setExpandedFormula] = useState<string | null>(null);

  const { data: dre, isLoading } = useQuery({
    queryKey: ["transport", "dre", from, to, filters.comparison],
    queryFn: () =>
      api
        .get<TransportDreDto>(
          `/dashboards/transport/dre?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}&comparison=${filters.comparison}`,
        )
        .catch(() => null),
    staleTime: 60_000,
  });

  // ── Deltas ────────────────────────────────────────────────────────────────
  const grossDelta  = computeDelta(dre?.grossRevenue,       dre?.prevGrossRevenue,       { direction: "higher_is_better" });
  const netDelta    = computeDelta(dre?.netRevenue,         dre?.prevNetRevenue,         { direction: "higher_is_better" });
  const marginDelta = computeDelta(dre?.contributionMargin, dre?.prevContributionMargin, { direction: "higher_is_better" });
  const resultDelta = computeDelta(dre?.netResult,          dre?.prevNetResult,          { direction: "higher_is_better" });
  const marginPctDelta = computeDelta(
    dre ? dre.contributionMarginPct * 100 : null,
    dre ? dre.prevContributionMarginPct * 100 : null,
    { direction: "higher_is_better" },
  );

  // ── Prev-value map para colunas de comparação na tabela DRE ───────────────
  // Derivamos o que não está no DTO via subtração dos escalares disponíveis
  const prevDirectCosts = dre
    ? dre.prevNetRevenue - dre.prevContributionMargin
    : 0;
  const prevIcms = dre
    ? dre.prevGrossRevenue - dre.prevNetRevenue
    : 0;

  const prevMap: Partial<Record<string, number>> = dre
    ? {
        gross_revenue:  dre.prevGrossRevenue,
        cte_revenue:    dre.prevGrossRevenue,
        deductions:     -prevIcms,
        icms:           -prevIcms,
        net_revenue:    dre.prevNetRevenue,
        direct_costs:   -prevDirectCosts,
        contribution:   dre.prevContributionMargin,
        net_result:     dre.prevNetResult,
      }
    : {};

  // ── Dados para gráficos ───────────────────────────────────────────────────
  const monthlyChartData = (dre?.monthly ?? []).map((m) => ({
    month: fmtMonthShort(m.month),
    "Receita Bruta":         m.grossRevenue,
    "Custos Diretos":        m.directCosts,
    "Margem Contribuição":   m.contributionMargin,
  }));

  // ── Totais acumulados 2026 ────────────────────────────────────────────────
  const total2026 = dre?.yearly2026.reduce(
    (acc, r) => ({
      cteCount:         acc.cteCount + r.cteCount,
      grossRevenue:     acc.grossRevenue + r.grossRevenue,
      icmsDeduction:    acc.icmsDeduction + r.icmsDeduction,
      netRevenue:       acc.netRevenue + r.netRevenue,
      directCosts:      acc.directCosts + r.directCosts,
      contribution:     acc.contribution + r.contributionMargin,
    }),
    { cteCount: 0, grossRevenue: 0, icmsDeduction: 0, netRevenue: 0, directCosts: 0, contribution: 0 },
  );

  // ── Labels de período ─────────────────────────────────────────────────────
  const periodLabel = `${fmtPeriodDate(from)} → ${fmtPeriodDate(new Date(new Date(to).getTime() - 1).toISOString())}`;

  return (
    <div className="space-y-6">

      {/* ── Alertas de confiabilidade ──────────────────────────────────────── */}
      {(dre?.warnings ?? []).length > 0 && (
        <div className="rounded-xl border border-warning-200 bg-warning-50 px-5 py-4 space-y-1.5">
          <div className="flex items-center gap-2 mb-0.5">
            <IconAlertTriangle className="w-4 h-4 text-warning-600 flex-shrink-0" />
            <p className="text-sm font-semibold text-warning-800">Avisos de confiabilidade</p>
          </div>
          {dre!.warnings.map((w, i) => (
            <p key={i} className="text-sm text-warning-700 pl-6 leading-relaxed">{w}</p>
          ))}
        </div>
      )}

      {/* ── Banner executivo — paleta Transmasi ──────────────────────────── */}
      <div className="rounded-xl bg-gradient-to-br from-tm-800 to-tm-700 text-white p-6 shadow-lg">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <div>
            <p className="text-[11px] font-semibold text-tm-200 uppercase tracking-widest">Período analisado</p>
            <p className="text-base font-semibold text-white mt-0.5">{periodLabel}</p>
          </div>
          <div className="flex items-center gap-3">
            {dre && (
              <span className="px-3 py-1.5 rounded-full bg-white/15 text-tm-100 text-xs font-medium">
                {dre.cteCount.toLocaleString("pt-BR")} CT-es autorizados
              </span>
            )}
            {dre?.dataCompleteness.cte && (
              <span className="px-3 py-1.5 rounded-full bg-white/15 text-tm-100 text-xs font-medium">
                Dados CT-e: OK
              </span>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
          <div className="border-l-2 border-white/20 pl-4">
            <p className="text-xs text-tm-100/70 mb-1">Receita Bruta CT-e</p>
            <p className="text-2xl font-bold text-white">
              {isLoading ? "Carregando…" : fmtBRL(dre?.grossRevenue ?? 0)}
            </p>
            {dre && dre.prevGrossRevenue > 0 && (
              <p className="text-xs text-tm-200 mt-1">
                vs. {fmtBRL(dre.prevGrossRevenue)} ({dre.comparisonLabel})
              </p>
            )}
          </div>

          <div className="border-l-2 border-white/20 pl-4">
            <p className="text-xs text-tm-100/70 mb-1">Receita Líquida <span className="text-tm-200">(após ICMS)</span></p>
            <p className="text-2xl font-bold text-white">
              {isLoading ? "—" : fmtBRL(dre?.netRevenue ?? 0)}
            </p>
            {dre && dre.icmsDeduction > 0 && (
              <p className="text-xs text-tm-200 mt-1">
                (-) ICMS: {fmtBRL(dre.icmsDeduction)}
              </p>
            )}
          </div>

          <div className="border-l-2 border-white/40 pl-4">
            <p className="text-xs text-tm-100/70 mb-1">
              Margem de Contribuição
              <span className="ml-1.5 text-tm-200 font-semibold text-[11px]">★ Dado confiável</span>
            </p>
            <p className="text-2xl font-bold text-white">
              {isLoading ? "—" : fmtBRL(dre?.contributionMargin ?? 0)}
            </p>
            {dre && dre.netRevenue > 0 && (
              <p className="text-xs text-tm-200 mt-1">
                {fmtPct(dre.contributionMarginPct * 100)} da receita líquida
              </p>
            )}
          </div>
        </div>
      </div>

      {/* ── KPIs confiáveis ────────────────────────────────────────────────── */}
      <div>
        <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-widest mb-3">
          Indicadores confiáveis
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
          <KPIStatCard
            label="Receita Bruta CT-e"
            value={dre ? fmtBRL(dre.grossRevenue) : "—"}
            delta={grossDelta}
            comparisonLabel={dre?.comparisonLabel ?? null}
            status="good"
            loading={isLoading}
          />
          <KPIStatCard
            label="Receita Líquida"
            value={dre ? fmtBRL(dre.netRevenue) : "—"}
            delta={netDelta}
            comparisonLabel={dre?.comparisonLabel ?? null}
            status="good"
            loading={isLoading}
          />
          <KPIStatCard
            label="Margem Contribuição ★"
            value={dre ? fmtBRL(dre.contributionMargin) : "—"}
            valueSub={dre ? fmtPct(dre.contributionMarginPct * 100) : undefined}
            delta={marginDelta}
            comparisonLabel={dre?.comparisonLabel ?? null}
            status={dre ? (dre.contributionMargin >= 0 ? "good" : "critical") : "neutral"}
            loading={isLoading}
          />
          <KPIStatCard
            label="Frete Motorista"
            value={dre ? fmtBRL(dre.driverFreightCost) : "—"}
            delta={null}
            comparisonLabel="custo direto — cartas frete"
            status="neutral"
            loading={isLoading}
          />
          <KPIStatCard
            label="Pedágios"
            value={dre ? fmtBRL(dre.tollCost) : "—"}
            delta={null}
            comparisonLabel="custo direto — CT-e"
            status="neutral"
            loading={isLoading}
          />
        </div>
      </div>

      {/* ── KPIs estimados ─────────────────────────────────────────────────── */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <IconAlertTriangle className="w-3.5 h-3.5 text-warning-500" />
          <p className="text-[11px] font-semibold text-warning-600 uppercase tracking-widest">
            Indicadores estimados — fact_payments inclui itens não operacionais
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <KPIStatCard
            label="Despesas Operacionais ⚠️"
            value={dre ? fmtBRL(dre.operationalExpenses) : "—"}
            delta={null}
            comparisonLabel="pagamentos realizados no período"
            status="warning"
            loading={isLoading}
          />
          <KPIStatCard
            label="Resultado Estimado ⚠️"
            value={dre ? fmtBRL(dre.netResult) : "—"}
            delta={resultDelta}
            comparisonLabel={dre?.comparisonLabel ?? null}
            status={dre ? (dre.netResult >= 0 ? "good" : "critical") : "neutral"}
            loading={isLoading}
          />
          <KPIStatCard
            label="Margem Operacional ⚠️"
            value={dre?.netMargin != null ? fmtPct(dre.netMargin * 100) : "—"}
            delta={null}
            comparisonLabel="resultado / receita líquida"
            status={
              dre?.netMargin != null
                ? dre.netMargin >= 0 ? "good" : "critical"
                : "neutral"
            }
            loading={isLoading}
          />
        </div>
      </div>

      {/* ── Tabela DRE hierárquica ─────────────────────────────────────────── */}
      <div className="card overflow-hidden">
        <div className="px-5 py-4 border-b border-surface-border flex items-center justify-between gap-4">
          <div>
            <p className="text-sm font-semibold text-gray-900">Demonstrativo de Resultado</p>
            <p className="text-xs text-gray-400 mt-0.5">
              Estrutura hierárquica · fonte de cada linha indicada · ⚠️ = dado estimado
            </p>
          </div>
          <div className="flex items-center gap-2">
            {dre && (
              <span className="text-xs text-gray-400 hidden sm:block">
                Período: {periodLabel}
              </span>
            )}
          </div>
        </div>

        {isLoading && (
          <div className="p-10 text-center text-sm text-gray-400 animate-pulse">
            Calculando DRE…
          </div>
        )}

        {!isLoading && !dre && (
          <div className="p-10 text-center text-sm text-gray-400">
            Sem dados disponíveis para o período selecionado.
          </div>
        )}

        {!isLoading && dre && (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 border-b border-surface-border">
                  <th className="px-5 py-3 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
                    Linha DRE
                  </th>
                  <th className="px-5 py-3 text-right text-[11px] font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    Valor período
                  </th>
                  <th className="px-5 py-3 text-right text-[11px] font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap hidden md:table-cell">
                    vs. {dre.comparisonLabel}
                  </th>
                  <th className="px-5 py-3 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider hidden lg:table-cell">
                    Fonte dos dados
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-surface-border">
                {dre.lines.map((line) => {
                  const drill = LINE_DRILL[line.key];
                  const onClick = !drill ? undefined : () => {
                    if ("cardKey" in drill) {
                      navigate(`/financeiro/memoria/${drill.cardKey}?periodFrom=${encodeURIComponent(from)}&periodTo=${encodeURIComponent(to)}`);
                    } else {
                      setExpandedFormula((cur) => (cur === line.key ? null : line.key));
                    }
                  };
                  return (
                    <React.Fragment key={line.key}>
                      <DreTableRow
                        line={line}
                        prevValue={prevMap[line.key]}
                        onClick={onClick}
                        drillHint={drill ? ("cardKey" in drill ? "docs" : "formula") : undefined}
                      />
                      {drill && "formula" in drill && expandedFormula === line.key && (
                        <tr className="bg-gray-50">
                          <td colSpan={4} className="px-8 py-2.5">
                            <code className="text-xs text-gray-600">{drill.formula}</code>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Legenda */}
        {dre && (
          <div className="px-5 py-3 border-t border-surface-border bg-gray-50/50 flex flex-wrap gap-4 text-xs text-gray-500">
            <span className="flex items-center gap-1.5">
              <span className="inline-block w-3 h-3 rounded-sm bg-tm-100 border border-tm-300" />
              Margem positiva
            </span>
            <span className="flex items-center gap-1.5">
              <span className="inline-block w-3 h-3 rounded-sm bg-danger-100 border border-danger-300" />
              Resultado negativo
            </span>
            <span className="flex items-center gap-1.5">
              <IconAlertTriangle className="w-3 h-3 text-warning-500" />
              Dado estimado — interpretar com cautela
            </span>
            <span className="flex items-center gap-1.5 ml-auto">
              <IconInfo className="w-3 h-3 text-gray-400" />
              ★ Margem de Contribuição é o indicador mais confiável desta DRE
            </span>
          </div>
        )}
      </div>

      {/* ── Gráficos ───────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

        {/* Tendência mensal */}
        <ChartCard
          title="Tendência Mensal"
          subtitle="Receita Bruta · Custos Diretos · Margem de Contribuição"
          height={260}
          loading={isLoading}
          isEmpty={monthlyChartData.length === 0}
          emptyMessage="Sem dados de tendência para exibir."
        >
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={monthlyChartData} margin={{ top: 5, right: 5, left: -10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis
                dataKey="month"
                tick={{ fontSize: 11, fill: "#9ca3af" }}
                tickLine={false}
                axisLine={false}
              />
              <YAxis
                tickFormatter={axisFormatter}
                tick={{ fontSize: 11, fill: "#9ca3af" }}
                tickLine={false}
                axisLine={false}
              />
              <Tooltip
                formatter={(v: number, name: string) => [fmtBRL(v), name]}
                contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e7ecef" }}
              />
              <Legend wrapperStyle={{ fontSize: 11, paddingTop: 8 }} />
              <Bar dataKey="Receita Bruta"       fill="#6366f1" radius={[3, 3, 0, 0]} />
              <Bar dataKey="Custos Diretos"      fill="#f97316" radius={[3, 3, 0, 0]} />
              <Bar dataKey="Margem Contribuição" fill="var(--brand)" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        {/* Ranking de clientes */}
        <div className="card overflow-hidden">
          <div className="px-5 py-4 border-b border-surface-border">
            <p className="text-sm font-semibold text-gray-900">Top Clientes por Receita CT-e</p>
            <p className="text-xs text-gray-400 mt-0.5">Faturamento bruto no período selecionado</p>
          </div>

          {isLoading && (
            <div className="p-6 text-sm text-gray-400 text-center">Carregando…</div>
          )}

          {!isLoading && (dre?.topCustomers ?? []).length === 0 && (
            <div className="p-6 text-sm text-gray-400 text-center">
              Sem dados de clientes no período.
            </div>
          )}

          <div className="divide-y divide-surface-border">
            {(dre?.topCustomers ?? []).slice(0, 8).map((c, i) => (
              <div key={i} className="px-5 py-3 flex items-center gap-3">
                <span className="text-xs font-bold text-gray-400 w-5 flex-shrink-0">{i + 1}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-800 truncate">{c.name}</p>
                  <div className="mt-1.5 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-tm-500 rounded-full transition-all"
                      style={{ width: `${Math.min(Math.max(c.sharePct, 2), 100)}%` }}
                    />
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-sm font-semibold text-gray-900">{c.formatted}</p>
                  <p className="text-xs text-gray-400">
                    {c.sharePct.toFixed(1)}% · {c.cteCount} CT-es
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Visão 2026 mês a mês ───────────────────────────────────────────── */}
      <div className="card overflow-hidden">
        <div className="px-5 py-4 border-b border-surface-border flex items-center justify-between gap-4">
          <div>
            <p className="text-sm font-semibold text-gray-900">Evolução 2026 — Mês a Mês</p>
            <p className="text-xs text-gray-400 mt-0.5">
              Apenas CT-e autorizados + Cartas Frete · Despesas operacionais não incluídas (dados incompletos)
            </p>
          </div>
          {total2026 && (
            <span className="text-xs text-gray-500 hidden sm:block flex-shrink-0">
              Acumulado: {fmtBRL(total2026.grossRevenue)}
            </span>
          )}
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-surface-border">
                <th className="px-4 py-3 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider w-20">
                  Mês
                </th>
                <th className="px-4 py-3 text-center text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
                  CT-es
                </th>
                <th className="px-4 py-3 text-right text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
                  Receita Bruta
                </th>
                <th className="px-4 py-3 text-right text-[11px] font-semibold text-gray-500 uppercase tracking-wider hidden sm:table-cell">
                  (-) ICMS
                </th>
                <th className="px-4 py-3 text-right text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
                  Rec. Líquida
                </th>
                <th className="px-4 py-3 text-right text-[11px] font-semibold text-gray-500 uppercase tracking-wider hidden md:table-cell">
                  (-) Custos
                </th>
                <th className="px-4 py-3 text-right text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
                  Margem
                </th>
                <th className="px-4 py-3 text-right text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
                  %
                </th>
              </tr>
            </thead>

            <tbody className="divide-y divide-surface-border">
              {(dre?.yearly2026 ?? Array.from({ length: 12 }, (_, i) => ({
                month: `2026-${String(i + 1).padStart(2, "0")}`,
                cteCount: 0, grossRevenue: 0, icmsDeduction: 0,
                netRevenue: 0, directCosts: 0, contributionMargin: 0,
                contributionMarginPct: 0,
              }))).map((row) => {
                const isEmpty = row.grossRevenue === 0;
                const positiveMargin = row.contributionMargin >= 0;

                return (
                  <tr
                    key={row.month}
                    className={
                      isEmpty
                        ? "opacity-40"
                        : "hover:bg-gray-50/60"
                    }
                  >
                    <td className="px-4 py-3 font-semibold text-gray-800 whitespace-nowrap">
                      {fmtMonth2026(row.month)}
                    </td>
                    <td className="px-4 py-3 text-center text-gray-600">
                      {isEmpty ? "—" : row.cteCount.toLocaleString("pt-BR")}
                    </td>
                    <td className="px-4 py-3 text-right font-medium text-gray-900 whitespace-nowrap">
                      {isEmpty ? "—" : fmtBRL(row.grossRevenue)}
                    </td>
                    <td className="px-4 py-3 text-right text-gray-500 hidden sm:table-cell whitespace-nowrap">
                      {isEmpty ? "—" : `(${fmtBRL(row.icmsDeduction)})`}
                    </td>
                    <td className="px-4 py-3 text-right text-gray-700 whitespace-nowrap">
                      {isEmpty ? "—" : fmtBRL(row.netRevenue)}
                    </td>
                    <td className="px-4 py-3 text-right text-gray-500 hidden md:table-cell whitespace-nowrap">
                      {isEmpty ? "—" : `(${fmtBRL(row.directCosts)})`}
                    </td>
                    <td className={`px-4 py-3 text-right font-semibold whitespace-nowrap ${
                      isEmpty ? "text-gray-400"
                      : positiveMargin ? "text-tm-600"
                      : "text-danger-600"
                    }`}>
                      {isEmpty ? "—" : fmtBRL(row.contributionMargin)}
                    </td>
                    <td className={`px-4 py-3 text-right text-xs font-semibold ${
                      isEmpty ? "text-gray-400"
                      : positiveMargin ? "text-tm-600"
                      : "text-danger-600"
                    }`}>
                      {isEmpty ? "—" : fmtPct(row.contributionMarginPct * 100)}
                    </td>
                  </tr>
                );
              })}
            </tbody>

            {/* Linha de total acumulado */}
            {total2026 && total2026.grossRevenue > 0 && (
              <tfoot>
                <tr className="border-t-2 border-gray-300 bg-gray-50 font-bold">
                  <td className="px-4 py-3 text-gray-900 font-bold text-sm">
                    TOTAL 2026
                  </td>
                  <td className="px-4 py-3 text-center text-gray-900">
                    {total2026.cteCount.toLocaleString("pt-BR")}
                  </td>
                  <td className="px-4 py-3 text-right text-gray-900 whitespace-nowrap">
                    {fmtBRL(total2026.grossRevenue)}
                  </td>
                  <td className="px-4 py-3 text-right text-gray-700 hidden sm:table-cell whitespace-nowrap">
                    ({fmtBRL(total2026.icmsDeduction)})
                  </td>
                  <td className="px-4 py-3 text-right text-gray-900 whitespace-nowrap">
                    {fmtBRL(total2026.netRevenue)}
                  </td>
                  <td className="px-4 py-3 text-right text-gray-700 hidden md:table-cell whitespace-nowrap">
                    ({fmtBRL(total2026.directCosts)})
                  </td>
                  <td className={`px-4 py-3 text-right whitespace-nowrap ${
                    total2026.contribution >= 0 ? "text-tm-700" : "text-danger-700"
                  }`}>
                    {fmtBRL(total2026.contribution)}
                  </td>
                  <td className={`px-4 py-3 text-right text-xs ${
                    total2026.contribution >= 0 ? "text-tm-700" : "text-danger-700"
                  }`}>
                    {total2026.netRevenue > 0
                      ? fmtPct((total2026.contribution / total2026.netRevenue) * 100)
                      : "—"}
                  </td>
                </tr>
              </tfoot>
            )}
          </table>

          {isLoading && (
            <p className="px-5 py-6 text-sm text-gray-400 text-center">
              Carregando evolução 2026…
            </p>
          )}
        </div>
      </div>

    </div>
  );
}

// ── Page wrapper com abas (DRE Gerencial · Custos) ─────────────────────────────

const TABS: Array<{ id: DreTab; label: string; icon: React.ComponentType<{ className?: string }> }> = [
  { id: "dre",    label: "DRE Gerencial", icon: IconLineChart },
  { id: "custos", label: "Custos",        icon: IconCalculator },
];

const SUBTITLES: Record<DreTab, string> = {
  dre:    "Demonstrativo gerencial de Receita, Custos e Margem de Contribuição",
  custos: "Pagamentos realizados, frete motorista, pedágios e fornecedores",
};

export function DREPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get("tab");
  const activeTab: DreTab = tabParam === "custos" ? "custos" : "dre";

  function selectTab(tab: DreTab) {
    setSearchParams(tab === "dre" ? {} : { tab }, { replace: true });
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="DRE / Resultado"
        subtitle={SUBTITLES[activeTab]}
      />

      {/* ── Barra de abas ──────────────────────────────────────────────────── */}
      <div className="border-b border-surface-border">
        <nav className="flex gap-1 -mb-px">
          {TABS.map((t) => {
            const isActive = activeTab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => selectTab(t.id)}
                className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                  isActive
                    ? "border-tm-500 text-tm-700"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                <t.icon className="w-4 h-4" />
                {t.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* ── Conteúdo da aba ────────────────────────────────────────────────── */}
      {activeTab === "dre" ? <DreManagerialTab /> : <CostsPage embedded />}
    </div>
  );
}
